import { linkOptions, useLocation } from '@tanstack/react-router';

import { CenteredPagination } from '../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { Tabs } from '../../cdl/Tabs/Tabs';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { PriceHeading } from '../../common/FormikProductTable/common/PriceHeading';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { formatMoney } from '../../common/helpers/formatMoney.helper';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { formatPrice } from '../../common/helpers/formatPrice.helper';
import { formatVessel } from '../../common/helpers/formatVessel.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { HistoricBuyerRefPopover } from '../../common/HistoricBuyerRefPopover/HistoricBuyerRefPopover';
import { useRole } from '../../common/hooks/useRole';
import { IconListDetails } from '../../common/icons/cdl/ListDetails';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../common/OfferStatusTag/OfferStatusTag';
import { TableEmptyState } from '../../common/TableEmptyState/TableEmptyState';
import { Box } from '../../common/ui/Box';
import { UnreadIndicator } from '../../order/common/UnreadIndicator';
import { Port } from '../../port/Port';
import { OfferModel } from '../model/OfferModel';
import { useOfferPagination } from '../useOfferPagination';

interface OfferOverviewTableFilters {
    supplierIds?: string[];
    customerIds: string[];
    searchQuery: string;
    states: string[];
    page: number;
    sortField?: string;
    sortDirection?: 'ASC' | 'DESC';
}

interface OfferOverviewTableProps {
    filters: OfferOverviewTableFilters;
    selectedTab: string;
    offersTotals: Record<string, number>;
    onTabSelect: (tab: string) => void;
    onPageChange: (page: number) => void;
}

export const OfferOverviewTable = ({
    filters,
    selectedTab,
    offersTotals,
    onTabSelect,
    onPageChange,
}: OfferOverviewTableProps) => {
    const role = useRole();
    const location = useLocation();
    const offerPaginationQuery = useOfferPagination({
        page: filters.page,
        searchQuery: filters.searchQuery,
        supplierIds: filters.supplierIds,
        customerIds: filters.customerIds,
        states: filters.states,
        sortField: filters.sortField,
        sortDirection: filters.sortDirection,
    });

    if (offerPaginationQuery.isPending) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={offersTotals} />
                <LoadingIndicator />
            </div>
        );
    }

    if (offerPaginationQuery.isError) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={offersTotals} />

                <AdvancedError />
            </div>
        );
    }

    if (!offerPaginationQuery.data?.content.length) {
        return (
            <div>
                <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={offersTotals} />
                <TableEmptyState Icon={IconListDetails} text={translate('order.emptylist')} />
            </div>
        );
    }

    return (
        <div>
            <Tabs selectedContext={selectedTab} onSelect={onTabSelect} totals={offersTotals} />

            <TableBuilder
                data={offerPaginationQuery.data.content}
                rowLink={(offer) =>
                    linkOptions({ to: '/offer/$id', params: { id: offer.id }, state: { originLocation: location } })
                }
                isLoading={offerPaginationQuery.isPlaceholderData && offerPaginationQuery.isFetching}
                grow={false}
            >
                {role.isSupplier() ? (
                    <TableBuilderColumn<OfferModel> header="" width="1%" hideOnLoading>
                        {(offer) => <Box width="8px">{!offer.supplierRead ? <UnreadIndicator /> : null}</Box>}
                    </TableBuilderColumn>
                ) : null}

                <TableBuilderColumn<OfferModel> header={translate('order.vendorreferenceshort')} width="10%" truncate>
                    {(offer) => offer.vendorReference ?? Characters.EN_DASH}
                </TableBuilderColumn>

                {!role.isSupplier() ? (
                    <TableBuilderColumn<OfferModel>
                        header={translate('order.buyerreferenceshort')}
                        width="10%"
                        truncate
                    >
                        {(offer) => (
                            <Box display="inline-flex" alignItems="center" gap={4}>
                                {offer.buyerReference || Characters.EN_DASH}
                                {offer.historicBuyerReferences?.length ? (
                                    <HistoricBuyerRefPopover references={offer.historicBuyerReferences} />
                                ) : null}
                            </Box>
                        )}
                    </TableBuilderColumn>
                ) : null}

                {role.isSuperAdmin() || !role.isOneCompanyUser() ? (
                    <TableBuilderColumn<OfferModel>
                        header={role.isSupplier() ? translate('offer.company') : translate('offer.supplier')}
                        width="10%"
                        truncate
                    >
                        {(offer) => formatCompanyName({ company: offer.supplier })}
                    </TableBuilderColumn>
                ) : null}

                <TableBuilderColumn<OfferModel> header={translate('offer.customer')} width="10%" truncate>
                    {(offer) => formatCompanyName({ company: offer.customer })}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.vessel')} width="15%" truncate>
                    {(offer) => formatVessel({ vessel: offer.vessel, short: true })}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.port')} width="10%">
                    {(offer) => {
                        if (!offer.port) {
                            return null;
                        }

                        return <Port port={offer.port} vesselId={offer.vesselId} locode={false} showTooltip />;
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.dateDeliveryShort')} width="10%">
                    {(offer) => {
                        if (!offer.dateDelivery) {
                            return null;
                        }

                        return formatDate({
                            date: offer.dateDelivery,
                            timeZone: 'UTC',
                        });
                    }}
                </TableBuilderColumn>

                {selectedTab === 'enquired' ? (
                    <TableBuilderColumn<OfferModel> header={translate('order.volume')} width="5%">
                        {(offer) => `${formatNumber({ number: offer.volume })}L`}
                    </TableBuilderColumn>
                ) : null}

                <TableBuilderColumn<OfferModel> header={<PriceHeading />} width="10%">
                    {(offer) => {
                        if (offer.ppl?.value && offer.ppl.value > 0) {
                            return formatPrice(offer.ppl);
                        }

                        return null;
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.total')} width="10%">
                    {(offer) => {
                        if (offer.total.value > 0) {
                            return formatMoney({
                                value: offer.total.value,
                                currency: offer.total.currency,
                                minimumFractionDigits: 2,
                            });
                        }

                        return null;
                    }}
                </TableBuilderColumn>

                {!['acknowledged', 'confirmed', 'delivered'].includes(selectedTab) ? (
                    <TableBuilderColumn<OfferModel> header={translate('order.status')} width="5%">
                        {(offer) => <OfferStatusTag state={offer.state} variant="supplier" />}
                    </TableBuilderColumn>
                ) : null}
            </TableBuilder>

            <CenteredPagination
                pageTotal={offerPaginationQuery.pageTotal}
                currentPage={filters.page}
                onPageChange={onPageChange}
            />
        </div>
    );
};
